import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/*
  ARCLIPSE Constellation — cinematic landing.
  Matches the live www.arclipse.com design language: dark, monochrome, Michroma + DM Sans,
  small wide-tracked type, full-bleed photographic panels with slow Ken Burns drift +
  scroll parallax + reveal-on-scroll. Dark sci-fi, Nolan/Zimmer restraint.
  All imagery is unique to Constellation (no reuse from the parent site).
  Styles are scoped under .cstl-root so they never collide with the app's global CSS.
*/

const FRONTIERS = [
  {
    id: 'space',
    kicker: 'Domain I',
    name: 'Space',
    desc: 'Orbit, launch, sensing, autonomy, and off-world operations where failure margins are thin.',
    img: 'assets/constellation/cstl-space.jpg',
    kb: 64,
    cs: 1.22,
  },
  {
    id: 'air',
    kicker: 'Domain II',
    name: 'Air',
    desc: 'Aviation, drones, high-altitude platforms, atmospheric sensing, and resilient mobility.',
    img: 'assets/constellation/cstl-air.jpg',
    kb: 58,
  },
  {
    id: 'sea',
    kicker: 'Domain III',
    name: 'Sea',
    desc: 'Subsea autonomy, ocean sensing, desalination, and pressure engineering in the harshest domain.',
    img: 'assets/constellation/cstl-sea.jpg',
    kb: 62,
  },
  {
    id: 'land',
    kicker: 'Domain IV',
    name: 'Land',
    desc: 'Energy, water, food, robotics, and infrastructure that makes life on Earth harder to break.',
    img: 'assets/constellation/cstl-land.jpg',
    kb: 66,
  },
];

const STEPS = [
  { step: '01', label: 'Signal', desc: 'Apply with what you are building and the evidence already visible.' },
  { step: '02', label: 'Selection', desc: 'Every application is reviewed by hand. Strong fits move to a founder call.' },
  { step: '03', label: 'Orbit', desc: 'A focused ten-week sprint to sharpen the company and close the gaps.' },
  { step: '04', label: 'D-DAY', desc: 'Arrive with clearer evidence in front of the people who change trajectories.' },
];

const CSS = `
.cstl-root {
  --c-black: #050403;
  --c-text: rgba(255,255,255,0.92);
  --c-muted: rgba(255,255,255,0.62);
  --c-dim: rgba(255,255,255,0.38);
  --c-line: rgba(255,255,255,0.15);
  position: relative;
  background: var(--c-black);
  color: var(--c-text);
  font-family: "DM Sans", system-ui, sans-serif;
  overflow-x: hidden;
}
.cstl-root *, .cstl-root *::before, .cstl-root *::after { box-sizing: border-box; }
.cstl-root img { display: block; max-width: 100%; }
.cstl-root a { color: inherit; text-decoration: none; }

/* faint technical grid */
.cstl-root::before {
  position: fixed; inset: 0; z-index: 1; pointer-events: none; content: "";
  background-image:
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 100px 100px; opacity: 0.5;
  mask-image: linear-gradient(180deg, transparent 0%, black 16%, black 74%, transparent 100%);
}

/* scroll progress */
.cstl-progress { position: fixed; top: 0; left: 0; z-index: 90; width: 100%; height: 2px; pointer-events: none; }
.cstl-progress span {
  display: block; height: 100%; width: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0.85), rgba(255,255,255,0.32));
  transform: scaleX(var(--p, 0)); transform-origin: 0 50%;
}

/* nav */
.cstl-nav {
  position: fixed; inset: 0 0 auto; z-index: 60;
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; padding: 20px 38px;
  transition: background .25s, border-color .25s, backdrop-filter .25s;
}
.cstl-nav.is-scrolled {
  background: rgba(5,4,3,0.4);
  border-bottom: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
}
.cstl-logo { display: inline-flex; align-items: center; }
.cstl-logo img { height: 16px; width: auto; max-width: 230px; }
.cstl-nav-links {
  position: absolute; left: 50%; transform: translateX(-50%);
  display: flex; align-items: center; gap: 32px; margin: 0; padding: 0; list-style: none;
}
.cstl-nav-links a, .cstl-cta, .cstl-rule, .cstl-kicker, .cstl-tag, .cstl-foot a, .cstl-steps-line {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
}
.cstl-nav-links a { position: relative; padding-bottom: 4px; color: rgba(255,255,255,0.74); transition: color .2s; }
.cstl-nav-links a::after {
  position: absolute; left: 0; right: 0; bottom: 0; height: 1px; content: "";
  background: rgba(255,255,255,0.85); transform: scaleX(0); transform-origin: 0 50%;
  transition: transform .3s cubic-bezier(.22,1,.36,1);
}
.cstl-nav-links a:hover { color: #fff; }
.cstl-nav-links a:hover::after, .cstl-nav-links a.is-active::after { transform: scaleX(1); }
.cstl-nav-links a.is-active { color: #fff; }

/* CTA — monochrome, matching the parent */
.cstl-cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 46px; padding: 15px 34px; border-radius: 3px; color: #fff;
  position: relative; overflow: hidden; isolation: isolate;
  background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.24);
  transition: transform .2s, border-color .2s, background .2s, box-shadow .2s;
}
.cstl-cta::after {
  position: absolute; inset: 0; z-index: -1; content: "";
  background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%);
  transform: translateX(-150%); transition: transform .85s cubic-bezier(.22,1,.36,1);
}
.cstl-cta:hover::after { transform: translateX(150%); }
.cstl-cta:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.48); box-shadow: 0 14px 38px rgba(0,0,0,0.45); }
.cstl-nav .cstl-cta { min-height: 38px; padding: 11px 22px; }

/* shared display type — small + wide tracking, like the live parent */
.cstl-display {
  font-family: "Michroma", system-ui, sans-serif; font-weight: 400;
  text-transform: uppercase; letter-spacing: 0.18em; color: var(--c-text);
}
.cstl-display em { color: #fff; font-style: normal; }
.cstl-rule {
  display: inline-flex; align-items: center; gap: 16px; color: rgba(255,255,255,0.7);
  margin-bottom: 22px;
}
.cstl-rule::before, .cstl-rule::after {
  width: 40px; height: 1px; content: "";
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
}

/* backdrop */
.cstl-backdrop { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
.cstl-backdrop img {
  width: 100%; height: 100%; object-fit: cover; object-position: center 55%;
  transform: translateY(var(--par, 0px)) scale(1.06);
  animation: cstl-drift 80s ease-in-out infinite alternate;
}
@keyframes cstl-drift { 0% { object-position: 50% 52%; } 100% { object-position: 50% 60%; } }

.cstl-stage { position: relative; z-index: 4; }
.cstl-content { position: relative; z-index: 10; }

/* hero */
.cstl-hero {
  min-height: 100svh; display: flex; align-items: flex-end; justify-content: flex-start;
  width: min(1180px, calc(100% - 64px)); margin: 0 auto;
  padding: 168px 0 16vh; text-align: left;
}
.cstl-hero-block { max-width: 680px; }
.cstl-kicker {
  display: inline-flex; align-items: center; gap: 14px; color: var(--c-dim);
  letter-spacing: 0.34em;
}
.cstl-kicker::after { width: 56px; height: 1px; content: ""; background: rgba(255,255,255,0.4); }
.cstl-hero-title {
  max-width: 700px; margin: 22px 0 0;
  font-size: clamp(18px, 2vw, 28px); letter-spacing: 0.2em; line-height: 1.5;
}
.cstl-hero-copy {
  max-width: 520px; margin: 22px 0 0; color: rgba(255,255,255,0.7);
  font-size: clamp(13px, 1vw, 15px); letter-spacing: 0.06em; line-height: 1.7;
}
.cstl-hero-actions { margin-top: 36px; display: flex; gap: 14px; flex-wrap: wrap; }
.cstl-scroll-cue {
  position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  color: var(--c-dim); font-size: 10px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase;
}
.cstl-scroll-cue::after {
  width: 1px; height: 42px; content: "";
  background: linear-gradient(180deg, rgba(255,255,255,0.55), transparent);
  animation: cstl-cue 2.2s ease-in-out infinite;
}
@keyframes cstl-cue {
  0% { transform: scaleY(0); transform-origin: 0 0; }
  45% { transform: scaleY(1); transform-origin: 0 0; }
  55% { transform: scaleY(1); transform-origin: 0 100%; }
  100% { transform: scaleY(0); transform-origin: 0 100%; }
}

/* intro moment */
.cstl-moment {
  min-height: 72svh; display: flex; align-items: center; justify-content: center;
  text-align: center; padding: 140px 24px;
}
.cstl-section-title { margin: 0; font-size: clamp(17px, 2vw, 26px); letter-spacing: 0.16em; line-height: 1.5; }
.cstl-moment-copy { margin: 22px auto 0; max-width: 600px; color: var(--c-muted); font-size: 14px; line-height: 1.85; letter-spacing: 0.04em; }

/* standard section + frontier collage */
.cstl-section { position: relative; width: min(1180px, calc(100% - 64px)); margin: 0 auto; padding: 130px 0; }
.cstl-section-head { max-width: 760px; margin: 0 auto 52px; text-align: center; }
.cstl-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; width: min(1100px, 100%); margin: 0 auto; }
.cstl-card {
  position: relative; aspect-ratio: 16 / 10; overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1); background: #000;
}
.cstl-card > img { width: 100%; height: 100%; object-fit: cover; transform: scale(var(--cs, 1.05)); transition: transform .9s cubic-bezier(.22,1,.36,1); }
.cstl-card:hover > img { transform: scale(calc(var(--cs, 1.05) + 0.07)); }
.cstl-card:hover { border-color: rgba(255,255,255,0.26); }
.cstl-card-scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(5,4,3,0.1) 0%, rgba(5,4,3,0.15) 40%, rgba(5,4,3,0.86) 100%); }
.cstl-card-body { position: absolute; left: 0; right: 0; bottom: 0; padding: 24px 26px 22px; }
.cstl-card-kicker {
  display: inline-flex; align-items: center; gap: 12px; color: var(--c-dim);
  font-family: "Michroma", system-ui, sans-serif; font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
}
.cstl-card-kicker::after { width: 40px; height: 1px; content: ""; background: rgba(255,255,255,0.4); }
.cstl-card-name {
  margin: 12px 0 10px; color: #fff; font-family: "Michroma", system-ui, sans-serif; font-weight: 400;
  font-size: clamp(18px, 2vw, 26px); letter-spacing: 0.14em; text-transform: uppercase;
}
.cstl-card-desc { margin: 0; max-width: 420px; color: rgba(255,255,255,0.74); font-size: 12.5px; letter-spacing: 0.03em; line-height: 1.65; }

/* full-bleed panels */
.cstl-panel {
  position: relative; min-height: 100svh; display: flex; align-items: flex-end;
  overflow: hidden; isolation: isolate;
}
.cstl-panel-bg { position: absolute; inset: -7% 0; z-index: -2; will-change: transform; }
.cstl-panel-bg img {
  width: 100%; height: 100%; object-fit: cover;
  animation: cstl-kb var(--kb, 60s) ease-in-out infinite alternate;
}
@keyframes cstl-kb { from { transform: scale(1.06); } to { transform: scale(1.14); } }
.cstl-panel-scrim {
  position: absolute; inset: 0; z-index: -1;
  background:
    linear-gradient(180deg, rgba(5,4,3,0.72) 0%, rgba(5,4,3,0.14) 30%, rgba(5,4,3,0.12) 55%, rgba(5,4,3,0.9) 100%),
    linear-gradient(90deg, rgba(5,4,3,0.58) 0%, rgba(5,4,3,0.06) 55%, rgba(5,4,3,0.28) 100%);
}
.cstl-panel-inner {
  position: relative; width: min(1180px, calc(100% - 64px)); margin: 0 auto; padding: 0 0 92px;
}
.cstl-panel-inner > * {
  opacity: 0; transform: translateY(34px); filter: blur(6px);
  transition: opacity .9s ease, transform .9s cubic-bezier(.22,1,.36,1), filter .9s ease;
}
.cstl-panel.is-in .cstl-panel-inner > * { opacity: 1; transform: translateY(0); filter: blur(0); }
.cstl-panel.is-in .cstl-panel-inner > *:nth-child(2) { transition-delay: .1s; }
.cstl-panel.is-in .cstl-panel-inner > *:nth-child(3) { transition-delay: .2s; }
.cstl-panel.is-in .cstl-panel-inner > *:nth-child(4) { transition-delay: .3s; }
.cstl-panel-kicker {
  display: inline-flex; align-items: center; gap: 14px; margin: 0 0 18px; color: var(--c-dim);
  font-family: "Michroma", system-ui, sans-serif; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase;
}
.cstl-panel-kicker::after { width: 60px; height: 1px; content: ""; background: rgba(255,255,255,0.45); }
.cstl-panel-name {
  margin: 0 0 16px; color: #fff; font-family: "Michroma", system-ui, sans-serif; font-weight: 400;
  font-size: clamp(20px, 2.4vw, 32px); letter-spacing: 0.16em; line-height: 1.25; text-transform: uppercase;
}
.cstl-panel-desc {
  max-width: 470px; margin: 0; color: rgba(255,255,255,0.76);
  font-size: clamp(13px, 1vw, 14px); letter-spacing: 0.04em; line-height: 1.75;
}

/* centered panel (programme / process / final) */
.cstl-panel--center { align-items: center; }
.cstl-panel--center .cstl-panel-inner { padding: 130px 0; text-align: center; }
.cstl-panel--center .cstl-panel-name { font-size: clamp(18px, 2vw, 27px); }

/* programme stages line */
.cstl-steps-line { margin-top: 30px; color: rgba(255,255,255,0.55); font-size: 11px; letter-spacing: 0.22em; }

/* process steps grid */
.cstl-steps {
  display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 16px;
  width: min(1020px, 100%); margin: 44px auto 0;
}
.cstl-step {
  padding: 26px 22px 30px; text-align: left;
  background: rgba(7,7,9,0.5); border: 1px solid rgba(255,255,255,0.12);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  transition: border-color .3s, background .3s;
}
.cstl-step:hover { border-color: rgba(255,255,255,0.26); background: rgba(12,12,14,0.62); }
.cstl-step-no { color: rgba(255,255,255,0.5); font-size: 11px; font-weight: 700; letter-spacing: 0.2em; }
.cstl-step h4 {
  margin: 14px 0 10px; color: #fff; font-family: "Michroma", system-ui, sans-serif; font-weight: 400;
  font-size: 14px; letter-spacing: 0.06em; text-transform: uppercase;
}
.cstl-step p { margin: 0; color: var(--c-muted); font-size: 12.5px; line-height: 1.62; }

/* statement */
.cstl-statement { align-items: center; text-align: center; min-height: 92svh; }
.cstl-statement .cstl-panel-inner { padding: 120px 0; }
.cstl-sigil {
  display: block; width: 46px; height: auto; margin: 0 auto 22px;
  opacity: 0.85; filter: drop-shadow(0 0 18px rgba(255,255,255,0.22));
}
.cstl-statement-title { max-width: 820px; margin: 22px auto 0; font-size: clamp(18px, 2.2vw, 28px); letter-spacing: 0.16em; line-height: 1.6; }
.cstl-statement-sub { margin: 26px auto 0; color: rgba(255,255,255,0.55); font-size: clamp(12px, 1vw, 14px); letter-spacing: 0.22em; text-transform: uppercase; }

/* lockup */
.cstl-lockup { display: block; width: min(420px, 70vw); height: auto; margin: 0 auto; filter: drop-shadow(0 0 24px rgba(255,255,255,0.08)); }

/* final + footer */
.cstl-final { align-items: center; min-height: 100svh; }
.cstl-final .cstl-panel-inner { padding: 0 0 18vh; text-align: center; }
.cstl-final-title { margin: 0; font-size: clamp(18px, 2.2vw, 28px); letter-spacing: 0.18em; line-height: 1.5; }
.cstl-foot {
  position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);
  width: min(1180px, calc(100% - 64px)); padding: 24px 0;
  border-top: 1px solid rgba(255,255,255,0.12);
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 24px;
}
.cstl-foot img { height: 32px; width: auto; opacity: 0.9; justify-self: start; }
.cstl-foot-center { display: flex; flex-direction: column; align-items: center; gap: 9px; text-align: center; }
.cstl-foot-label { color: var(--c-dim); font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 700; }
.cstl-foot-nav { display: flex; gap: 20px; flex-wrap: wrap; justify-content: center; }
.cstl-foot-nav a { color: var(--c-dim); font-size: 10px; transition: color .25s; }
.cstl-foot-nav a:hover { color: rgba(255,255,255,0.88); }
.cstl-foot-copy { justify-self: end; text-align: right; color: var(--c-dim); font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 700; }

/* reveal */
.cstl-reveal { opacity: 0; transform: translateY(28px); filter: blur(6px); transition: opacity .8s ease, transform .8s cubic-bezier(.22,1,.36,1), filter .8s ease; }
.cstl-reveal.is-in { opacity: 1; transform: translateY(0); filter: blur(0); }

@media (max-width: 980px) {
  .cstl-nav-links { display: none; }
  .cstl-steps { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 600px) {
  .cstl-nav { padding: 14px 18px; }
  .cstl-logo img { height: 12px; }
  .cstl-hero { width: min(100% - 32px, 1180px); padding-top: 120px; }
  .cstl-section { width: min(100% - 32px, 1180px); padding: 84px 0; }
  .cstl-grid { grid-template-columns: 1fr; }
  .cstl-panel-inner { width: min(100% - 32px, 1180px); padding-bottom: 64px; }
  .cstl-panel { min-height: 88svh; }
  .cstl-steps { grid-template-columns: 1fr; }
  .cstl-foot { display: flex; flex-direction: column; gap: 18px; text-align: center; }
  .cstl-foot img { justify-self: center; }
  .cstl-foot-copy { justify-self: center; text-align: center; }
}
@media (prefers-reduced-motion: reduce) {
  .cstl-backdrop img, .cstl-panel-bg img, .cstl-scroll-cue::after { animation: none; }
  .cstl-reveal, .cstl-panel-inner > * { opacity: 1; transform: none; filter: none; transition: none; }
}
`;

export default function EvolvingLanding() {
  const navRef = useRef(null);
  const barRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const docEl = document.documentElement;
    const nav = navRef.current;
    const bar = barRef.current;
    const backdrop = backdropRef.current;
    const panelBgs = Array.prototype.slice.call(document.querySelectorAll('.cstl-panel-bg'));
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const vh = window.innerHeight;
        if (nav) nav.classList.toggle('is-scrolled', y > 40);
        const max = docEl.scrollHeight - vh;
        if (bar && max > 0) bar.style.setProperty('--p', Math.min(1, y / max));
        if (backdrop && !reduce) backdrop.style.setProperty('--par', (y * 0.05).toFixed(1) + 'px');
        if (!reduce) {
          for (let i = 0; i < panelBgs.length; i++) {
            const rect = panelBgs[i].parentElement.getBoundingClientRect();
            if (rect.bottom < -80 || rect.top > vh + 80) continue;
            const offset = (rect.top + rect.height / 2 - vh / 2) * 0.1;
            panelBgs[i].style.transform = 'translateY(' + offset.toFixed(1) + 'px)';
          }
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    const links = Array.prototype.slice.call(document.querySelectorAll('.cstl-nav-links a'));
    const sections = links.map((l) => document.querySelector(l.getAttribute('href'))).filter(Boolean);
    let activeObs;
    if ('IntersectionObserver' in window && sections.length) {
      activeObs = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const id = '#' + e.target.id;
          links.forEach((l) => l.classList.toggle('is-active', l.getAttribute('href') === id));
        });
      }, { rootMargin: '-45% 0px -50% 0px' });
      sections.forEach((s) => activeObs.observe(s));
    }

    const reveals = Array.prototype.slice.call(document.querySelectorAll('.cstl-reveal, .cstl-panel'));
    let revealObs;
    if ('IntersectionObserver' in window) {
      revealObs = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealObs.unobserve(e.target); }
        });
      }, { threshold: 0.18 });
      reveals.forEach((r) => revealObs.observe(r));
    } else {
      reveals.forEach((r) => r.classList.add('is-in'));
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (activeObs) activeObs.disconnect();
      if (revealObs) revealObs.disconnect();
    };
  }, []);

  return (
    <div className="cstl-root">
      <style>{CSS}</style>

      <div className="cstl-backdrop" aria-hidden="true">
        <img ref={backdropRef} src="assets/constellation/cstl-hero.jpg" alt="" />
      </div>

      <div className="cstl-progress" aria-hidden="true"><span ref={barRef} /></div>

      <div className="cstl-stage">
        <nav className="cstl-nav" ref={navRef} aria-label="Primary">
          <a className="cstl-logo" href="#top" aria-label="ARCLIPSE Constellation home">
            <img src="assets/arclipse-wordmark-white.png" alt="ARCLIPSE" />
          </a>
          <ul className="cstl-nav-links" aria-label="Section navigation">
            <li><a href="#frontiers">Frontiers</a></li>
            <li><a href="#programme">Programme</a></li>
            <li><a href="#process">Process</a></li>
          </ul>
          <Link className="cstl-cta" to="/apply">Apply</Link>
        </nav>

        <main className="cstl-content" id="top">
          {/* HERO */}
          <section className="cstl-hero" aria-labelledby="cstl-hero-title">
            <div className="cstl-hero-block">
              <div className="cstl-reveal"><span className="cstl-kicker">Arclipse Constellation</span></div>
              <h1 id="cstl-hero-title" className="cstl-display cstl-hero-title cstl-reveal">
                The frontier needs <em>founders.</em>
              </h1>
              <div className="cstl-reveal" style={{ transitionDelay: '.1s' }}>
                <p className="cstl-hero-copy">
                  A selective programme for the companies the future depends on, before
                  the market knows what to call them.
                </p>
              </div>
              <div className="cstl-reveal cstl-hero-actions" style={{ transitionDelay: '.2s' }}>
                <Link className="cstl-cta" to="/apply">Apply for Cohort 1</Link>
              </div>
            </div>
            <div className="cstl-scroll-cue" aria-hidden="true">Scroll</div>
          </section>

          {/* INTRO MOMENT */}
          <section className="cstl-moment">
            <div className="cstl-reveal">
              <div className="cstl-rule">The programme</div>
              <h2 className="cstl-display cstl-section-title">Every Arclipse story begins here.</h2>
              <p className="cstl-moment-copy">
                Constellation finds the founders building resilient frontier systems across
                space, air, sea, and land, and gives them ten weeks to become undeniable.
              </p>
            </div>
          </section>

          {/* FRONTIER COLLAGE */}
          <section className="cstl-section" id="frontiers">
            <div className="cstl-reveal">
              <div className="cstl-section-head">
                <div className="cstl-rule">The frontiers</div>
                <h2 className="cstl-display cstl-section-title">Where founders build.</h2>
              </div>
            </div>
            <div className="cstl-grid">
              {FRONTIERS.map((f, i) => (
                <article key={f.id} className="cstl-card cstl-reveal" style={{ transitionDelay: (i * 0.08) + 's', '--cs': f.cs || 1.05 }}>
                  <img src={f.img} alt="" loading="lazy" />
                  <div className="cstl-card-scrim" aria-hidden="true" />
                  <div className="cstl-card-body">
                    <span className="cstl-card-kicker">{f.kicker}</span>
                    <h3 className="cstl-card-name">{f.name}</h3>
                    <p className="cstl-card-desc">{f.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* PROGRAMME */}
          <section className="cstl-panel cstl-panel--center" id="programme" style={{ '--kb': '70s' }}>
            <div className="cstl-panel-bg"><img src="assets/constellation/cstl-founder.jpg" alt="" loading="lazy" /></div>
            <div className="cstl-panel-scrim" aria-hidden="true" />
            <div className="cstl-panel-inner">
              <span className="cstl-panel-kicker" style={{ justifyContent: 'center' }}>What it is</span>
              <h3 className="cstl-display cstl-panel-name">Ten weeks. One cohort.</h3>
              <p className="cstl-panel-desc" style={{ margin: '0 auto' }}>
                Selected founders enter a focused sprint to sharpen the company, strengthen
                the evidence, and prepare for D-DAY. Capital, structure, and conviction.
              </p>
              <div className="cstl-steps-line">
                Apply&nbsp;&nbsp;·&nbsp;&nbsp;Founder call&nbsp;&nbsp;·&nbsp;&nbsp;Ten-week sprint&nbsp;&nbsp;·&nbsp;&nbsp;D-DAY
              </div>
            </div>
          </section>

          {/* PROCESS */}
          <section className="cstl-panel cstl-panel--center" id="process" style={{ '--kb': '74s' }}>
            <div className="cstl-panel-bg"><img src="assets/constellation/cstl-path.jpg" alt="" loading="lazy" /></div>
            <div className="cstl-panel-scrim" aria-hidden="true" />
            <div className="cstl-panel-inner">
              <div className="cstl-rule" style={{ justifyContent: 'center' }}>The path</div>
              <h3 className="cstl-display cstl-panel-name">From first signal to D-DAY.</h3>
              <div className="cstl-steps">
                {STEPS.map((s) => (
                  <article className="cstl-step" key={s.step}>
                    <span className="cstl-step-no">{s.step}</span>
                    <h4>{s.label}</h4>
                    <p>{s.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* STATEMENT */}
          <section className="cstl-panel cstl-statement">
            <div className="cstl-panel-bg"><img src="assets/constellation/cstl-statement.jpg" alt="" loading="lazy" /></div>
            <div className="cstl-panel-scrim" aria-hidden="true" />
            <div className="cstl-panel-inner">
              <img className="cstl-sigil" src="assets/arclipse-emblem-white.png" alt="" aria-hidden="true" />
              <div className="cstl-rule" style={{ justifyContent: 'center' }}>Who we back</div>
              <h2 className="cstl-display cstl-statement-title">We back the ones who go first.</h2>
              <p className="cstl-statement-sub">Before it is obvious. Before it is safe.</p>
            </div>
          </section>

          {/* FINAL */}
          <section className="cstl-panel cstl-final" style={{ '--kb': '76s' }}>
            <div className="cstl-panel-bg"><img src="assets/constellation/cstl-final.jpg" alt="" loading="lazy" /></div>
            <div className="cstl-panel-scrim" aria-hidden="true" />
            <div className="cstl-panel-inner">
              <img className="cstl-lockup" src="assets/arclipse-constellation-lockup.png" alt="ARCLIPSE Constellation" />
              <h2 className="cstl-display cstl-final-title" style={{ marginTop: 28 }}>
                Begin your <em>journey.</em>
              </h2>
              <div className="cstl-hero-actions" style={{ justifyContent: 'center', marginTop: 32 }}>
                <Link className="cstl-cta" to="/apply">Apply for Cohort 1</Link>
              </div>
            </div>
            <footer className="cstl-foot">
              <img src="assets/arclipse-lockup-white.png" alt="ARCLIPSE" />
              <div className="cstl-foot-center">
                <div className="cstl-foot-label">A frontier initiative by ARCLIPSE</div>
                <nav className="cstl-foot-nav" aria-label="Legal and contact">
                  <a href="https://www.arclipse.com/">Arclipse</a>
                  <Link to="/privacy">Privacy</Link>
                  <Link to="/terms">Terms</Link>
                  <a href="mailto:contact@arclipse.com">Contact</a>
                </nav>
              </div>
              <div className="cstl-foot-copy">2026 Constellation</div>
            </footer>
          </section>
        </main>
      </div>
    </div>
  );
}
